import { useState, useEffect } from 'react';
import { Paper, Typography, Button, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@utils/analytics';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const InstallBanner = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only show on mobile/tablet or ChromeOS — not on desktop Windows/macOS/Linux.
    // UA-based detection: a blanket `maxTouchPoints > 0` also matched touch-screen
    // Windows laptops. The `/Mac/ + maxTouchPoints > 1` clause is the special case
    // for iPadOS 13+, which ships a desktop ("Macintosh") UA — real desktop Macs
    // report 0 touch points.
    const ua = navigator.userAgent;
    const isMobileOrTablet =
      /Android|iPhone|iPad|iPod/i.test(ua) || (/Mac/i.test(ua) && navigator.maxTouchPoints > 1);
    const isChromeOS = /CrOS/i.test(ua);
    if (!isMobileOrTablet && !isChromeOS) return;

    // Check if dismissed recently (within 7 days)
    const dismissed = localStorage.getItem('install-banner-dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
      trackEvent('install_prompt_shown');
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      trackEvent('install_accepted');
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('install-banner-dismissed', String(Date.now()));
    trackEvent('install_dismissed');
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Paper
      elevation={2}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: { xs: 1.5, sm: 2 },
        mb: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: 2,
      }}
    >
      <GetAppIcon sx={{ fontSize: 28, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t('install.title', 'Install GetEmoji')}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.9 }}>
          {t('install.message', 'Add to your home screen for quick access')}
        </Typography>
      </Box>
      <Button
        variant="contained"
        size="small"
        onClick={handleInstall}
        sx={{
          bgcolor: 'white',
          color: '#667eea',
          fontWeight: 600,
          flexShrink: 0,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
        }}
      >
        {t('install.button', 'Install')}
      </Button>
      <IconButton size="small" onClick={handleDismiss} sx={{ color: 'white', flexShrink: 0 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
};
